import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  ChevronRight,
  Target,
  ChevronLeft,
} from 'lucide-react';
import { saveAs } from 'file-saver';
import type { CVData, ParsedJobOffer } from './types';
// defaultCV / cloneCvData / makeTemplateId are consumed inside the hooks now.
// exportPdfViaApi is consumed inside VisualPanel.
// client-cache helpers are consumed directly inside the AI hooks.
// ProLayout / TypstPreview / marginStyles are owned by <PreviewPane>.
// DatePicker / YearPicker moved to <EditSidebar>.
// Header is consumed inside <PreviewPane>.
import { useJobTarget } from './hooks/useJobTarget';
import { useAiAnalyses } from './hooks/useAiAnalyses';
import { useCvState } from './hooks/useCvState';
import { useEditorUi } from './hooks/useEditorUi';
import { useNavigate } from 'react-router-dom';
import { useCoverLetter } from './hooks/useCoverLetter';
import { useCvGeneration } from './hooks/useCvGeneration';
import { useCvTranslation } from './hooks/useCvTranslation';
import { useAutoIa } from './hooks/useAutoIa';
import { useCvFileAnalysis } from './hooks/useCvFileAnalysis';
import { useCvEditing } from './hooks/useCvEditing';
import { useCvVersions } from './hooks/useCvVersions';
import { printCvNode } from './lib/print-cv';
import { buildAtsCvDocx } from './lib/docx-exporter';
import { cvDataToText } from './lib/cv-text';
import './index.css';
import { getT, I18nProvider } from "./contexts/I18nContext"
import { LoginModal } from "./components/LoginModal"
import { useApp } from "./contexts/AppContext"
import { useAuth } from "./contexts/AuthContext"
import { createClient } from "./lib/supabase/client"
// Header is consumed inside <PreviewPane>.
import { Toaster } from "sonner"
import { ExtensionPageModal } from "./components/modals/ExtensionPageModal"
import { SettingsModal } from "./components/SettingsModal"
import { PipelineProgressModal } from "./components/modals/PipelineProgressModal"
import { MobileHeader } from "./components/layout/MobileHeader"
import { MobileBottomBar } from "./components/layout/MobileBottomBar"
import { VisualPanel } from "./components/panels/VisualPanel"
import { JobTargetSection } from "./components/panels/JobTargetSection"
import { CoverLetterSection } from "./components/panels/CoverLetterSection"
import { AiAnalyzeSection } from "./components/panels/AiAnalyzeSection"
import { AtsGenerateSection } from "./components/panels/AtsGenerateSection"
import { AnalyseCompleteAtsSection } from "./components/panels/AnalyseCompleteAtsSection"
import { OptionsSection } from "./components/panels/OptionsSection"
import { EditSidebar } from "./components/panels/EditSidebar"
import { PreviewPane } from "./components/panels/PreviewPane"
import { SimpleModeEditor } from "./components/SimpleMode/SimpleModeEditor"
import { EditionMenu } from "./components/panels/EditionMenu"
import { SimpleActionRail, type SimpleModalType } from "./components/SimpleMode/SimpleActionRail"
import { MiseEnPagePanel } from "./components/SimpleMode/MiseEnPagePanel"
import { AdaptToJobModal } from "./components/SimpleMode/modals/AdaptToJobModal"
import { AtsScoreModal } from "./components/SimpleMode/modals/AtsScoreModal"
import { TranslateModal } from "./components/SimpleMode/modals/TranslateModal"
import { CoverLetterModal } from "./components/SimpleMode/modals/CoverLetterModal"
import { ExportModal } from "./components/SimpleMode/modals/ExportModal"
import { JobOffersModal } from "./components/SimpleMode/modals/JobOffersModal"
import { useJobOffersSync as useJobOffers } from "./hooks/useJobOffersSync"
import { parsedOfferToAnalysisText } from './lib/job-url-scraper'
import { formatDateWithFullMonth, calculateAge, extractCityFromCompany } from './lib/date-format'
import { CvDiffViewer } from './components/SimpleMode/CvDiffViewer';
import { Modal } from './components/ui/Modal';
import { useCredits } from './hooks/useCredits';
import { FreemiumGate } from './components/ui/FreemiumGate';
import type { CreditType } from './lib/credits-store';
import { useSettings } from './contexts/SettingsContext';
import { useUserSync } from './hooks/useUserSync';
import { useCvSync } from './hooks/useCvSync';
import { ModalProvider } from './contexts/ModalContext';
import { NotifProvider } from './contexts/NotifContext';

export function App() {
  const { settings } = useSettings();

  // CV editor core state: templates, versions, common info, field visibility,
  // and the active CVData — all persisted to localStorage and kept in sync by
  // useCvState.
  const cvState = useCvState();
  const {
    activeTemplate,
    setActiveTemplate,
    templateMeta,
    setTemplateMeta,
    allTemplatesData,
    setAllTemplatesData,
    commonInfo,
    setCommonInfo,
    fieldVisibility,
    setFieldVisibility,
    data,
    setData,
    isSyncingDataRef,
  } = cvState;
  void isSyncingDataRef;

  // newTemplateName / editingTemplateId / editingTemplateName are owned by useCvVersions.

  // Editor UI shell: sections accordion, sidebars, mobile tabs, layout,
  // preview engine, theme, locale. All persistence (localStorage + <html>
  // data-theme/lang side effects) lives in useEditorUi.
  const editorUi = useEditorUi({ activeTemplate });


  // Phase B — new-user redirect to /wizard.
  //
  // Heuristic:
  // - If cv_wizard_completed is already set (done/skipped), the user has made
  //   a choice — no redirect, they stay on /app.
  // - Otherwise, check cv_all_templates_data: if it exists in localStorage,
  //   the user already has edited data (existing user pre-wizard feature).
  //   We auto-mark them as 'skipped' so they're never bothered again.
  // - If neither, they're a truly new user → redirect to /wizard.
  //
  // Runs exactly once at mount.
  const navigate = useNavigate();
  const wizardRedirectAttemptedRef = useRef(false);
  useEffect(() => {
    if (wizardRedirectAttemptedRef.current) return;
    wizardRedirectAttemptedRef.current = true;
    try {
      const completed = window.localStorage.getItem('cv_wizard_completed');
      if (completed === 'done' || completed === 'skipped') return;
      const hasExistingData = window.localStorage.getItem('cv_all_templates_data') !== null;
      if (hasExistingData) {
        // Existing user from before the wizard existed: mark as skipped silently.
        window.localStorage.setItem('cv_wizard_completed', 'skipped');
        return;
      }
      // Truly new user: redirect to the wizard.
      navigate('/wizard', { replace: true });
    } catch {
      /* ignore */
    }
  }, [navigate]);
  const {
    sectionsExpanded,
    setSectionsExpanded,
    toggleSection,
    isSidebarOpen,
    setIsSidebarOpen,
    isAtsSidebarOpen,
    setIsAtsSidebarOpen,
    isVisuelSidebarOpen,
    setIsVisuelSidebarOpen,
    isMiseEnPageOpen,
    setIsMiseEnPageOpen,
    mobileTab,
    setMobileTab,
    mobilePreviewSubTab,
    setMobilePreviewSubTab,
    layoutTemplate,
    setLayoutTemplate,
    previewEngine,
    setPreviewEngine,
    theme,
    setTheme,
    locale,
    setLocale,
  } = editorUi;

  const [showExtensionPage, setShowExtensionPage] = useState(false);
  const [extensionInstalled, setExtensionInstalled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const logoMenuButtonRef = useRef<HTMLButtonElement>(null);
  const [logoMenuPosition, setLogoMenuPosition] = useState<{ top: number; left: number } | null>(null);
  // Translate version state and handler are provided by useCvTranslation below.
  const [isMobile, setIsMobile] = useState(false);
  const { isLoginModalOpen, setIsLoginModalOpen, setUserId } = useApp();
  const { signIn, user: authUser, signOut: authSignOut, resetPasswordForEmail } = useAuth();
  const t = getT(locale);

  // Use the CV's own language for template labels, contact line, and date formatting.
  const activeCvLang = useMemo(
    () => templateMeta.find((m) => m.id === activeTemplate)?.lang ?? locale,
    [templateMeta, activeTemplate, locale],
  );
  const cvT = useMemo(() => getT(activeCvLang), [activeCvLang]);

  // Locale-aware date formatter — passes activeCvLang to formatDateWithFullMonth.
  const formatDateCv = useCallback(
    (d: string) => formatDateWithFullMonth(d, activeCvLang),
    [activeCvLang],
  );

  // Sync SettingsContext.locale → useEditorUi locale (single source of truth)
  useEffect(() => {
    setLocale(settings.locale);
  }, [settings.locale]); // eslint-disable-line react-hooks/exhaustive-deps

  // Analytics user identification
  useUserSync();

  // Sync CV automatique avec Supabase (login → charge serveur, modif → push debounced)
  useCvSync(cvState);

  // Phase 2+3 — Job offers & CVxJob relation management
  const jobOffersHook = useJobOffers();
  const { addOffer: addJobOffer } = jobOffersHook;
  // Track which saved offer is currently being worked on (for CVxJob recording)
  const [selectedJobOfferId, setSelectedJobOfferId] = useState<string | null>(null);
  // Signal AdaptToJobModal to auto-start adaptation (set when coming from "Offres" modal)
  const [adaptAutoRun, setAdaptAutoRun] = useState(false);

  // Phase 7 — Freemium credits
  const creditsHook = useCredits();
  const [freemiumGateAction, setFreemiumGateAction] = useState<CreditType | null>(null);
  const mobileSectionsInitialized = useRef(false);
  const originalViewportContent = useRef<string | null>(null);
  const [previewUserScale, setPreviewUserScale] = useState(1);
  const pinchRef = useRef<{ initialDist: number; initialScale: number } | null>(null);
  const getTouchDistance = (e: React.TouchEvent) => {
    if (e.touches.length < 2) return 0;
    const a = e.touches[0]!;
    const b = e.touches[1]!;
    return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
  };
  const handlePreviewTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) pinchRef.current = { initialDist: getTouchDistance(e), initialScale: previewUserScale };
  };
  const _handlePreviewTouchMove = (e: React.TouchEvent) => {
    if (pinchRef.current && e.touches.length === 2) {
      e.preventDefault();
      const currentDist = getTouchDistance(e);
      if (pinchRef.current.initialDist <= 0) return;
      const ratio = currentDist / pinchRef.current.initialDist;
      let newScale = pinchRef.current.initialScale * ratio;
      newScale = Math.max(1, Math.min(3, newScale));
      setPreviewUserScale(newScale);
    }
  };
  const handlePreviewTouchEnd = (e: React.TouchEvent) => {
    if (e.touches.length < 2) {
      const hadPinch = !!pinchRef.current;
      pinchRef.current = null;
      if (hadPinch && previewViewportRef.current) {
        const el = previewViewportRef.current;
        requestAnimationFrame(() => {
          const maxLeft = el.scrollWidth - el.clientWidth;
          const maxTop = el.scrollHeight - el.clientHeight;
          if (maxLeft > 0) el.scrollLeft = maxLeft / 2;
          if (maxTop > 0) el.scrollTop = maxTop / 2;
        });
      }
    }
  };
  useEffect(() => {
    const el = previewViewportRef.current;
    if (!el || !isMobile) return;
    const onMove = (e: TouchEvent) => {
      if (pinchRef.current && e.touches.length === 2) {
        e.preventDefault();
        const currentDist = Math.hypot(e.touches[1]!.clientX - e.touches[0]!.clientX, e.touches[1]!.clientY - e.touches[0]!.clientY);
        if (pinchRef.current.initialDist <= 0) return;
        const ratio = currentDist / pinchRef.current.initialDist;
        let newScale = pinchRef.current.initialScale * ratio;
        newScale = Math.max(1, Math.min(3, newScale));
        setPreviewUserScale(newScale);
      }
    };
    el.addEventListener('touchmove', onMove, { passive: false });
    return () => el.removeEventListener('touchmove', onMove);
  }, [isMobile]);
  /* À l'ouverture de l'onglet Aperçu mobile: scroll pour voir le haut du CV (marge = 0 à l'échelle 1, sinon 794*(S-1)/2) */
  useEffect(() => {
    if (!isMobile || mobileTab !== 'preview' || mobilePreviewSubTab !== 'apercu') return;
    const el = previewViewportRef.current;
    if (!el) return;
    const id = requestAnimationFrame(() => {
      const scaleStr = getComputedStyle(document.documentElement).getPropertyValue('--preview-scale').trim() || '0.5';
      const S = parseFloat(scaleStr) * previewUserScale;
      const margin = Math.max(0, 794 * (S - 1) / 2);
      el.scrollLeft = margin;
      el.scrollTop = margin;
    });
    return () => cancelAnimationFrame(id);
  }, [isMobile, mobileTab, mobilePreviewSubTab, previewUserScale]);
  // theme / locale persistence and their <html> side effects live in useEditorUi.
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === 'ATSmyCV_EXTENSION_INSTALLED') setExtensionInstalled(true);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);
  useEffect(() => {
    if (!mobileMenuOpen) {
      // Leave logoMenuPosition stale; it's only read while the menu is open.
      return;
    }
    const btn = logoMenuButtonRef.current;
    if (btn) {
      const r = btn.getBoundingClientRect();
      setLogoMenuPosition({ top: r.bottom + 4, left: r.left });
    }
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (mobileMenuRef.current?.contains(target)) return;
      if ((e.target as HTMLElement)?.closest?.('[data-portal="logo-menu"]')) return;
      setMobileMenuOpen(false);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [mobileMenuOpen]);
  // The "close translate popover on outside click" effect is placed next to
  // the useCvTranslation hook call further down, because it depends on
  // translatePopoverId which is owned by that hook.
  useEffect(() => {
    if (isMobile && !mobileSectionsInitialized.current) {
      mobileSectionsInitialized.current = true;
      setSectionsExpanded({
        profilCommun: false,
        optimisationAts: false,
        options: false,
        profil: false,
        experiences: false,
        formation: false,
        plus: false,
        modelesCv: true, /* seul Versions déployé */
        template: false,
        offreEmploiCible: false,
        scorePreview: false,
        analyseCompleteAts: false,
        aiAnalyze: false,
        atsGenerate: false,
        coverLetter: false,
      });
    }
  }, [isMobile, setSectionsExpanded]);
  const [srOnlyLabelsEnabled, setSrOnlyLabelsEnabled] = useState(() => {
    const saved = localStorage.getItem('cv_sr_only_labels');
    if (saved != null) return saved === 'true';
    return true;
  });
  useEffect(() => {
    localStorage.setItem('cv_sr_only_labels', String(srOnlyLabelsEnabled));
  }, [srOnlyLabelsEnabled]);
  // toggleSection is provided by useEditorUi.

  // Helper function to format location from city and country
  const getFormattedLocation = () => {
    const city = fieldVisibility.city && commonInfo.city ? commonInfo.city : '';
    const country = fieldVisibility.country && commonInfo.country ? commonInfo.country : '';
    if (city && country) return `${city}, ${country}`;
    if (city) return city;
    if (country) return country;
    return commonInfo.location || '';
  };

  // "Born" / "Né" / "Née" based on CV language and gender.
  const getBornPrefix = () => {
    if (activeCvLang === 'en') return cvT('profile.bornPrefix');
    const gender = data.personalInfo.gender?.toLowerCase() || '';
    const civ = data.personalInfo.civility || '';
    if (gender.includes('femme') || civ === 'Mme.') return cvT('profile.bornPrefixFemale');
    return cvT('profile.bornPrefix');
  };

  const getFullNameForExport = () => {
    const fn = (data.personalInfo.firstName ?? '').trim();
    const ln = (data.personalInfo.lastName ?? '').trim();
    if (fn || ln) return `${fn} ${ln}`.trim();
    return (data.personalInfo.fullName ?? '').trim() || 'CV';
  };

  // Ligne contact pour ATS / export (uniquement champs visibles)
  // Format ATS: "Vit à XXX", "Né à Paris, Nationalité Française", "Homme, 39 ans, Célibataire"
  const getContactLineATS = () => {
    const parts: string[] = [];
    if (fieldVisibility.email && data.personalInfo.email) parts.push(data.personalInfo.email);
    if (fieldVisibility.phone && data.personalInfo.phone) parts.push(data.personalInfo.phone);
    const loc = getFormattedLocation();
    if (loc) parts.push(`${cvT('profile.livesIn')} ${loc}`);
    // Born in X, Nationality Y
    const birthNatParts: string[] = [];
    if (fieldVisibility.birthPlace && data.personalInfo.birthPlace) birthNatParts.push(`${getBornPrefix()} ${cvT('experience.inCity')} ${data.personalInfo.birthPlace}`);
    if (fieldVisibility.nationality && data.personalInfo.nationality) birthNatParts.push(`${cvT('profile.nationality')} ${data.personalInfo.nationality}`);
    if (birthNatParts.length > 0) parts.push(birthNatParts.join(', '));
    // Regrouper: Genre, âge, statut sur une même partie
    const personalParts: string[] = [];
    if (fieldVisibility.gender && data.personalInfo.gender) personalParts.push(data.personalInfo.gender);
    if (fieldVisibility.birthDate && data.personalInfo.birthDate) {
      const age = calculateAge(data.personalInfo.birthDate);
      personalParts.push(age ? `${age} ${cvT('profile.yearsOld')}` : data.personalInfo.birthDate);
    }
    if (fieldVisibility.maritalStatus && data.personalInfo.maritalStatus) personalParts.push(data.personalInfo.maritalStatus);
    if (personalParts.length > 0) parts.push(personalParts.join(', '));
    return parts.join(' | ');
  };

  // Build a single text representation of CV for AI endpoints.
  // Delegates to the pure lib function cvDataToText (src/lib/cv-text.ts).
  // Memoized: only recreated when `t` changes (locale switch).
  const cvDataToResumeText = useCallback((cv: CVData): string => cvDataToText(cv, cvT), [cvT]);

  // Fingerprint helpers + fetchWithFallback are owned by the AI hooks that
  // need them (useAiAnalyses, useCvTranslation, useAutoIa).
  // formatDateWithFullMonth, calculateAge, extractCityFromCompany are imported
  // from src/lib/date-format.ts (pure functions, no component state needed).

  const cvRef = useRef<HTMLDivElement>(null);
  const previewViewportRef = useRef<HTMLDivElement>(null);
  // isSyncingDataRef is owned by useCvState and destructured above.
  // analyzeDebounceRef is internal to useCvFileAnalysis.

  useEffect(() => {
    let rafId: number | null = null;
    let lastUpdate = 0;
    const THROTTLE_MS = 100;

    const update = () => {
      const now = performance.now();
      if (now - lastUpdate < THROTTLE_MS) return;
      lastUpdate = now;

      if (typeof window === 'undefined') return;
      const w = window.visualViewport?.width ?? window.innerWidth;
      const fitWidth = Math.max(200, w);
      const scale = fitWidth / 794;
      document.documentElement.style.setProperty('--preview-width-px', `${fitWidth}px`);
      document.documentElement.style.setProperty('--preview-scale', String(scale));
    };

    update();

    const onResize = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        update();
      });
    };

    window.addEventListener('resize', onResize, { passive: true });
    return () => {
      window.removeEventListener('resize', onResize);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);
  useEffect(() => {
    if (typeof document === 'undefined' || !isMobile) return;
    const meta = document.querySelector('meta[name="viewport"]');
    if (!meta) return;
    if (originalViewportContent.current === null) originalViewportContent.current = meta.getAttribute('content');
    meta.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover');
    return () => {
      if (meta && originalViewportContent.current !== null) meta.setAttribute('content', originalViewportContent.current);
    };
  }, [isMobile]);

  // CV version management (create / rename / delete / duplicate / import
  // from JSON backup) + the local rename-in-progress state live in
  // useCvVersions.
  const cvVersions = useCvVersions({
    activeTemplate,
    templateMeta,
    allTemplatesData,
    setActiveTemplate,
    setTemplateMeta,
    setAllTemplatesData,
    t,
  });
  const {
    handleDeleteTemplate,
    handleDuplicateTemplate,
    renameTemplate,
  } = cvVersions;

  // data / activeTemplate / templateMeta / allTemplatesData / commonInfo /
  // fieldVisibility state and persistence effects live in useCvState.
  // layoutTemplate / previewEngine (global + per-template) persistence is in useEditorUi.

  // Simple mode: which focused modal is open (null = none)
  // Phase 1.5 — open adapt modal if coming from wizard with ?adapt=1
  const [activeSimpleModal, setActiveSimpleModal] = useState<SimpleModalType>(() => {
    if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('adapt') === '1') {
      return 'adapt';
    }
    return null;
  });
  // Clean ?adapt=1 from URL without re-rendering
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('adapt') === '1') {
      navigate('/app', { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Diff viewer state: shown after Auto IA creates a new version
  const [autoDiffData, setAutoDiffData] = useState<{ before: CVData; after: CVData } | null>(null);

  const [isExporting, setIsExporting] = useState(false);
  const [isExportingPremium, setIsExportingPremium] = useState(false);
  const [exportMenuValue, setExportMenuValue] = useState('');
  const [pageMode, _setPageMode] = useState(false);
  const [maxPages, setMaxPages] = useState<number | null>(null);
  const [contentHeight, setContentHeight] = useState(0);
  const PAGE_HEIGHT_PX = 1123; // 297mm en pixels
  const [marginSize, setMarginSize] = useState<'none' | 'small' | 'medium' | 'large'>('medium');

  // Marges selon le choix (reserved for future use)
  const _marginStyles = {
    none: { padding: '0' },
    small: { padding: '12px 15px' },
    medium: { padding: '23px 30px' },
    large: { padding: '40px 50px' }
  };
  // Job target state (paste / URL / title + parsed offer + fetch/parse actions).
  // All persistence to localStorage is handled inside the hook, keyed on activeTemplate.
  const jobTarget = useJobTarget(activeTemplate);
  const {
    jobDescription,
    jobUrl,
    jobTitle,
    jobInputMode,
    parsedJobOffer,
    isFetchingUrl: _isFetchingUrl,
    isParsingJobWithAi,
    rawJobTextForAi,
    canParseJobWithAi,
    effectiveJobDescription,
    hasFullAnnouncement: _hasFullAnnouncement,
    setJobDescription,
    setJobUrl,
    setJobTitle,
    setJobInputMode,
    setParsedJobOffer,
    fetchUrl: _handleFetchUrl,
    parseJobWithAi: handleParseJobWithAi,
  } = jobTarget;
  void _hasFullAnnouncement;
  // isAutoIaRunning / runAnalyzeIaAfterJob / pipelineProgress / availableModels
  // are owned by useAutoIa (wired below, after its prerequisite hooks).

  // Preview analysis, file upload analysis and AI parse-to-CV are all in
  // useCvFileAnalysis (wired below, once its prereqs are in scope).

  // AI analyses (analyze / compare / optimize) — state, cache, fingerprints,
  // stale detection and run actions all live in useAiAnalyses.
  const aiAnalyses = useAiAnalyses({
    activeTemplate,
    cvData: data,
    effectiveJobDescription: effectiveJobDescription || '',
    getResumeText: cvDataToResumeText,
  });
  const {
    aiAnalysisResult,
    aiAnalysisStructured,
    analysisFp: _analysisFp,
    isAiAnalyzing,
    showAnalyzeCached,
    isAnalyzeStale,
    setAiAnalysisStructured,
    aiCompareResult,
    aiCompareStructured,
    compareFp: _compareFp,
    isAiComparing,
    showCompareCached,
    isCompareStale,
    setAiCompareStructured,
    aiOptimizeResult,
    aiOptimizeStructured,
    optimizeFp: _optimizeFp,
    isAiOptimizing,
    showOptimizeCached,
    isOptimizeStale,
    setAiOptimizeStructured,
    currentFingerprint: _currentFingerprint,
    runAnalyze: handleAiAnalyzeClick,
    runCompare: handleAiCompareClick,
    runOptimize: handleAiOptimizeClick,
  } = aiAnalyses;


  // layoutTemplate + previewEngine per-template persistence is in useEditorUi.

  // Job state persistence (jobDescription / jobUrl / jobTitle / jobInputMode /
  // parsedJobOffer) is owned by useJobTarget — keyed on activeTemplate there.


  // Phase 3.2 — Record CVxJob score when AI analysis completes for a selected offer
  useEffect(() => {
    if (selectedJobOfferId && aiAnalysisStructured?.atsScore != null) {
      jobOffersHook.recordAnalysis(activeTemplate, selectedJobOfferId, aiAnalysisStructured.atsScore);
    }
  }, [aiAnalysisStructured]); // eslint-disable-line react-hooks/exhaustive-deps

  // isAiComparing / isAiOptimizing are provided by useAiAnalyses above.
  // isAiParsing is in useCvFileAnalysis (wired below).
  // Generation state and handlers (runGenerate + applyGenerated + presets)
  // live in useCvGeneration.
  const cvGeneration = useCvGeneration({
    activeTemplate,
    cvData: data,
    effectiveJobDescription: effectiveJobDescription || '',
    getResumeText: cvDataToResumeText,
    parsedJobOffer,
    jobTitle,
    templateMeta,
    setAllTemplatesData,
    setTemplateMeta,
    setActiveTemplate,
    defaultAtsPreset: settings.defaultAtsPreset,
  });
  const {
    atsSectionConfig,
    setAtsSectionConfig,
    atsAdaptationLevel,
    setAtsAdaptationLevel,
    applyPresetAts: handlePresetAts,
    isGenerating,
    generatedCvData,
    generateChangesSummary,
    generateWarnings,
    runGenerate: handleGenerateClick,
    applyGenerated: handleApplyGenerated,
    applyGeneratedPartial: handleApplyGeneratedPartial,
  } = cvGeneration;

  // Cover letter state, persistence and handler are in useCoverLetter.
  const coverLetter = useCoverLetter({
    activeTemplate,
    templateMeta,
    allTemplatesData,
    locale,
    effectiveJobDescription: effectiveJobDescription || '',
    aiAnalysisStructured,
    aiCompareStructured,
    aiOptimizeStructured,
    getResumeText: cvDataToResumeText,
  });
  const {
    coverLetterLength,
    setCoverLetterLength,
    coverLetterFidelity,
    setCoverLetterFidelity,
    coverLetterTone,
    setCoverLetterTone,
    generatedCoverLetterByVersionId,
    setGeneratedCoverLetterByVersionId,
    isGeneratingCoverLetter,
    runCoverLetter: handleCoverLetterClick,
  } = coverLetter;
  // setGeneratedCoverLetterByVersionId is consumed by <CoverLetterSection>.

  // atsSectionConfig / atsAdaptationLevel (+ persistence) are owned by useCvGeneration.
  // availableModels is owned by useAutoIa and destructured below.

  // useAutoIa orchestrates the manual "Auto IA" flow and the extension
  // "Analyse IA" pipeline. It needs setters from 5 other hooks — expressed
  // here explicitly so the coupling stays visible.
  const autoIa = useAutoIa({
    activeTemplate,
    cvData: data,
    getResumeText: cvDataToResumeText,
    effectiveJobDescription: effectiveJobDescription || '',
    rawJobTextForAi,
    parsedJobOffer,
    jobTitle,
    templateMeta,
    locale,
    atsSectionConfig,
    atsAdaptationLevel,
    coverLetterLength,
    coverLetterFidelity,
    coverLetterTone,
    setAiAnalysisStructured,
    setAiCompareStructured,
    setAiOptimizeStructured,
    setAllTemplatesData,
    setTemplateMeta,
    setActiveTemplate,
    setParsedJobOffer,
    setJobDescription,
    setGeneratedCoverLetterByVersionId,
    onVersionCreated: (before, after) => setAutoDiffData({ before, after }),
  });
  const {
    isAutoIaRunning,
    pipelineProgress,
    setPipelineProgress,
    setRunAnalyzeIaAfterJob,
    availableModels,
    runAutoIa: handleAutoIa,
  } = autoIa;

  // effectiveJobDescription / rawJobTextForAi / canParseJobWithAi are derived
  // in useJobTarget. currentFingerprint + isAnalyzeStale/isCompareStale/
  // isOptimizeStale are provided by useAiAnalyses.

  // Real-time preview analysis is owned by useCvFileAnalysis (wired below).
  const cvFileAnalysis = useCvFileAnalysis({
    cvData: data,
    effectiveJobDescription: effectiveJobDescription || '',
    setData,
  });
  const {
    previewResult,
    completeAnalysisResult,
    isAnalyzingFile,
    showCompleteAnalysis,
    setShowCompleteAnalysis,
    lastExtractedText,
    isAiParsing,
    handleFileUpload,
    handleAiParseClick,
  } = cvFileAnalysis;
  void lastExtractedText;
  void setShowCompleteAnalysis;

  // Réception automatique de l'annonce depuis un onglet (bookmarklet sur Indeed / extension)
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      // Silent save from extension "Sauvegarder" button (via chrome.storage.onChanged)
      if (event.data?.type === 'ATSmyCV_SAVE_JOB_SILENT' && event.data?.payload) {
        const p = event.data.payload as ParsedJobOffer;
        if (p && (p.description || p.titre)) {
          addJobOffer({
            title: p.titre || 'Offre sans titre',
            company: p.entreprise || '',
            source: 'extension',
            rawText: parsedOfferToAnalysisText(p),
            parsedData: p,
            isFavorite: true,
          });
        }
        return;
      }
      if (event.data?.type !== 'ATSmyCV_JOB' || !event.data?.payload) return;
      const payload = event.data.payload as ParsedJobOffer & { analyzeWithAi?: boolean; openAdaptAutoRun?: boolean };
      const analyzeWithAi = Boolean(payload?.analyzeWithAi);
      const openAdaptAutoRun = Boolean(payload?.openAdaptAutoRun);
      const p = payload && typeof payload === 'object'
        ? { ...payload, analyzeWithAi: undefined, openAdaptAutoRun: undefined } as ParsedJobOffer
        : null;
      if (!p || (!p.description && !p.titre)) return;
      setParsedJobOffer(p);
      setJobDescription((p.description && p.description.trim().length >= 50) ? p.description.trim() : parsedOfferToAnalysisText(p));
      setJobInputMode('paste');
      setSectionsExpanded((prev) => ({ ...prev, offreEmploiCible: true }));
      setIsAtsSidebarOpen(true);
      if (analyzeWithAi) setRunAnalyzeIaAfterJob(true);
      if (openAdaptAutoRun) {
        setAdaptAutoRun(true);
        setActiveSimpleModal('adapt');
      }
      // Add to job offers list (extension "browse" → auto-saved)
      addJobOffer({
        title: p.titre || 'Offre sans titre',
        company: p.entreprise || '',
        source: 'extension',
        rawText: parsedOfferToAnalysisText(p),
        parsedData: p,
        isFavorite: false,
      });
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [setParsedJobOffer, setJobDescription, setJobInputMode, setSectionsExpanded, setIsAtsSidebarOpen, setRunAnalyzeIaAfterJob, setAdaptAutoRun, setActiveSimpleModal, addJobOffer]);

  // rawJobTextForAi / canParseJobWithAi are provided by useJobTarget.

  // The extension "Analyse IA" pipeline (triggered when runAnalyzeIaAfterJob
  // flips to true) lives inside useAutoIa.

  // Mesurer la hauteur du contenu pour le mode page
  useEffect(() => {
    if (cvRef.current) {
      const measure = () => {
        if (cvRef.current) {
          setContentHeight(cvRef.current.scrollHeight);
        }
      };
      measure();
      // Re-mesurer après un court délai pour les images/fonts
      const timer = setTimeout(measure, 200);
      return () => clearTimeout(timer);
    }
  }, [data, layoutTemplate, pageMode]);

  // Calculer le nombre de pages
  const totalPages = Math.max(1, Math.ceil(contentHeight / PAGE_HEIGHT_PX));
  const pagesToShow = maxPages ? Math.min(maxPages, totalPages) : totalPages;

  // handleFetchUrl and handleParseJobWithAi are provided by useJobTarget.

  // handleAutoIa is provided by useAutoIa (aliased from runAutoIa).

  // Handle file upload for complete analysis (PDF or DOCX)
  // Calqué sur le fonctionnement ATS: parsing OCR/NLP du document
  // handleFileUpload is provided by useCvFileAnalysis.

  // handleAiAnalyzeClick / handleAiCompareClick / handleAiOptimizeClick are
  // provided by useAiAnalyses (aliased from runAnalyze/runCompare/runOptimize).

  // handleGenerateClick / handleApplyGenerated are provided by useCvGeneration.
  // handleCoverLetterClick is provided by useCoverLetter (aliased from runCoverLetter).
  // handlePresetAts is provided by useCvGeneration (aliased from applyPresetAts).

  // Phase 7 — Credit-gated wrappers (memoized to avoid inline recreations on every render)
  const guardedAnalyze = useCallback(() => {
    if (!creditsHook.tryConsume('aiAnalysis')) {
      setFreemiumGateAction('aiAnalysis');
      return;
    }
    handleAiAnalyzeClick();
  }, [creditsHook, handleAiAnalyzeClick]);

  const guardedAutoIa = useCallback(() => {
    if (!creditsHook.tryConsume('optimize')) {
      setFreemiumGateAction('cvGeneration');
      return;
    }
    handleAutoIa();
  }, [creditsHook, handleAutoIa]);

  const cvTranslation = useCvTranslation({
    templateMeta,
    allTemplatesData,
    getResumeText: cvDataToResumeText,
    setAllTemplatesData,
    setTemplateMeta,
    setActiveTemplate,
  });
  const {
    translatePopoverId,
    setTranslatePopoverId,
    isTranslatingVersionId,
    translateVersion: handleTranslateVersion,
  } = cvTranslation;

  // Close the translate popover on outside click (relocated here because
  // it depends on translatePopoverId from useCvTranslation above).
  useEffect(() => {
    if (!translatePopoverId) return;
    const onDocClick = () => setTranslatePopoverId(null);
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [translatePopoverId, setTranslatePopoverId]);

  // handleAiParseClick is provided by useCvFileAnalysis.

  // Stable modal close handler — avoids inline arrow function recreation on every render
  const handleCloseActiveModal = useCallback(() => setActiveSimpleModal(null), []);

  // Stable computed: active template name (used in JSX + print filename, avoid .find() on every render)
  const activeVersionName = useMemo(
    () => templateMeta.find((m) => m.id === activeTemplate)?.name ?? activeTemplate,
    [templateMeta, activeTemplate],
  );

  const getPrintFilename = () => {
    const full = getFullNameForExport().trim();
    const words = full ? full.split(/\s+/).filter(Boolean) : [];
    const initials =
      words.length >= 2
        ? (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase()
        : words.length === 1 && words[0]!.length >= 2
          ? words[0]!.slice(0, 2).toUpperCase()
          : words.length === 1 && words[0]!.length === 1
            ? words[0]!.toUpperCase() + words[0]!.toUpperCase()
            : 'CV';
    const versionName = (activeVersionName ?? 'CV')
      .replace(/\s+/g, '_')
      .replace(/[^\w\u00C0-\u024F\-_]/g, '')
      .slice(0, 40) || 'Version';
    return `CV_${initials}_${versionName}`;
  };

  const handlePrint = (mode: 'graphique' | 'ats' = 'graphique') => {
    const printTitle = getPrintFilename();

    if (mode === 'ats') setLayoutTemplate('ats');
    setIsExporting(true);
    if (isMobile) {
      setMobileTab('preview');
      setMobilePreviewSubTab('apercu');
    }

    // Defer so React can re-render (branch A pages → branch B continuous)
    // and attach cvRef to the new element. Then query the DOM directly
    // (don't rely on the React ref, which can be stale across re-renders
    // on mobile) via the .cv-container class.
    setTimeout(() => {
      // Prefer cvRef if still valid, otherwise fall back to querySelector
      let node: HTMLElement | null = cvRef.current;
      if (!node) {
        node = document.querySelector<HTMLElement>('.cv-container');
      }
      if (!node) {
        console.warn('[handlePrint] no CV container found');
        setIsExporting(false);
        return;
      }
      printCvNode(node, {
        title: printTitle,
        onDone: () => setIsExporting(false),
      });
    }, isMobile ? 500 : 50);
  };

  const handleExportJSON = () => {
    const dataStr = JSON.stringify({ ...data, exportDate: new Date().toISOString() }, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const exportFileDefaultName = `CV_${getFullNameForExport().replace(/\s+/g, '_')}_Backup.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  // Export DOCX optimisé ATS (format recommandé par les ATS) — construit par buildAtsCvDocx (lib pur).
  const handleExportDOCX = async () => {
    const fullName = getFullNameForExport();
    const blob = await buildAtsCvDocx({
      data,
      fieldVisibility,
      contactLine: getContactLineATS(),
      fullName,
      extractCityFromCompany,
      formatDateWithFullMonth: formatDateCv,
      formatEducationDate,
    });
    saveAs(blob, `CV_${fullName.replace(/\s+/g, '_')}_ATS.docx`);
  };

  // CV field-level CRUD helpers (personal info, experience, education,
  // skills, languages, hobbies, avatar upload) live in useCvEditing.
  const cvEditing = useCvEditing({
    data,
    setData,
    setCommonInfo,
    setFieldVisibility,
    activeTemplate,
    setTemplateMeta,
  });

  // Helper: format an education entry's date range for display.
  // Kept in App.tsx because it's consumed both by the DOCX exporter
  // handler (handleExportDOCX) and by the EditSidebar props.
  const formatEducationDate = (edu: { date?: string; startDate?: string; endDate?: string }) => {
    if (edu.startDate && edu.endDate) return `${edu.startDate} ${cvT('experience.dateTo')} ${edu.endDate}`;
    if (edu.startDate) return edu.startDate;
    if (edu.date) return edu.date;
    return '';
  };

  return (
    <I18nProvider locale={locale} t={t}>
      <ModalProvider value={{ activeModal: activeSimpleModal, setActiveModal: setActiveSimpleModal }}>
      <NotifProvider>
      <Toaster richColors position="top-right" theme={theme === 'light' ? 'light' : 'dark'} toastOptions={{ className: 'font-sans' }} />
      <SettingsModal />
      <div className={`app-root min-h-screen min-h-[100dvh] md:min-h-screen flex flex-col ${isMobile ? 'is-mobile' : ''} ${theme === 'light' ? 'theme-light bg-[var(--app-bg)] text-[var(--app-fg)]' : 'bg-[#0a0a0a] text-white'}`}>
      <ExtensionPageModal
        showExtensionPage={showExtensionPage}
        onClose={() => setShowExtensionPage(false)}
        t={t}
      />
      <PipelineProgressModal
        pipelineProgress={pipelineProgress}
        onClose={() => {
          setPipelineProgress(null);
          // In simple mode: if auto-IA just succeeded, reopen the adapt modal
          // so the user sees the success screen instead of nothing.
          if (pipelineProgress?.kind === 'autoIa' && pipelineProgress.successMessage && !pipelineProgress.successMessage.startsWith('Erreur')) {
            setActiveSimpleModal('adapt');
          }
        }}
      />
        {!isExporting && isMobile && (
          <MobileHeader
            mobileMenuRef={mobileMenuRef}
            logoMenuButtonRef={logoMenuButtonRef}
            logoMenuPosition={logoMenuPosition}
            mobileMenuOpen={mobileMenuOpen}
            mobileTab={mobileTab}
            mobilePreviewSubTab={mobilePreviewSubTab}
            theme={theme}
            locale={locale}
            authUser={authUser}
            cvVersionCount={templateMeta.length}
            templateMeta={templateMeta}
            activeTemplate={activeTemplate}
            setActiveTemplate={setActiveTemplate}
            onLogoMenuToggle={() => setMobileMenuOpen((prev) => !prev)}
            onThemeChange={(newTheme) => { setTheme(newTheme); setMobileMenuOpen(false); }}
            onLocaleChange={(newLocale) => { setLocale(newLocale); setMobileMenuOpen(false); }}
            onMobileTabChange={setMobileTab}
            onMobilePreviewSubTabChange={setMobilePreviewSubTab}
            onLoginClick={() => { setMobileMenuOpen(false); setIsLoginModalOpen(true); }}
            onLogoutClick={() => { setMobileMenuOpen(false); void authSignOut(); }}
            t={t}
          />
        )}
        {/* Local save banner — shown to non-connected users only */}
        {!authUser && !isMobile && !isExporting && (
          <div className="flex items-center justify-between gap-3 px-4 py-2 bg-amber-500/10 border-b border-amber-500/20 text-amber-300 text-xs shrink-0">
            <span className="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="size-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              {t('banner.localSave')}
            </span>
            <button
              type="button"
              onClick={() => setIsLoginModalOpen(true)}
              className="shrink-0 px-3 py-1 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/30 text-amber-200 font-semibold transition-colors"
            >
              {t('auth.login')}
            </button>
          </div>
        )}

        {/* Simple mode layout (desktop only) */}
        {!isMobile && (
          <SimpleModeEditor
            isSidebarOpen={isSidebarOpen}
            leftRail={
              <EditionMenu
                t={t}
                data={data}
                commonInfo={commonInfo}
                fieldVisibility={fieldVisibility}
                setData={setData}
                editing={cvEditing}
                templateMeta={templateMeta}
                activeTemplate={activeTemplate}
                setActiveTemplate={setActiveTemplate}
                onRenameTemplate={renameTemplate}
                onDuplicateTemplate={handleDuplicateTemplate}
                onDeleteTemplate={handleDeleteTemplate}
                getBestScoreForCv={jobOffersHook.getBestScoreForCv}
              />
            }
            atsPanel={
              !isExporting && (
                <div
                  className={`shrink-0 transition-all duration-300 ease-in-out overflow-hidden ${
                    isAtsSidebarOpen ? 'w-[400px]' : 'w-0'
                  }`}
                >
                  <div className="h-full border-l border-white/10 glass overflow-y-auto no-print scrollbar-hide">
                    <div className="p-4 md:p-6 space-y-4">
                      <div className="space-y-4 pt-2">
                        <h1
                          className="flex items-center justify-between gap-2 text-xl font-black tracking-tight text-white cursor-pointer hover:text-emerald-300 transition-colors mb-2"
                          onClick={() => setIsAtsSidebarOpen((prev) => !prev)}
                        >
                          <span className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-emerald-600/20 flex items-center justify-center text-emerald-400">
                              <Target size={18} />
                            </div>
                            {t('sections.optimisationAts')}
                          </span>
                          <ChevronRight size={16} />
                        </h1>
                      </div>
                      <JobTargetSection
                        t={t}
                        isMobile={false}
                        isExpanded={sectionsExpanded.offreEmploiCible}
                        onToggleExpanded={() => toggleSection('offreEmploiCible')}
                        extensionInstalled={extensionInstalled}
                        onShowExtensionPage={() => setShowExtensionPage(true)}
                        jobInputMode={jobInputMode}
                        setJobInputMode={setJobInputMode}
                        parsedJobOffer={parsedJobOffer}
                        setParsedJobOffer={setParsedJobOffer}
                        jobDescription={jobDescription}
                        setJobDescription={setJobDescription}
                        jobUrl={jobUrl}
                        setJobUrl={setJobUrl}
                        jobTitle={jobTitle}
                        setJobTitle={setJobTitle}
                        canParseJobWithAi={canParseJobWithAi}
                        isParsingJobWithAi={isParsingJobWithAi}
                        onParseJobWithAi={handleParseJobWithAi}
                        isAutoIaRunning={isAutoIaRunning}
                        onAutoIa={handleAutoIa}
                        effectiveJobDescription={effectiveJobDescription || ''}
                      />
                      <AnalyseCompleteAtsSection
                        t={t}
                        isExpanded={sectionsExpanded.analyseCompleteAts}
                        onToggleExpanded={() => toggleSection('analyseCompleteAts')}
                        previewResult={previewResult}
                        completeAnalysisResult={completeAnalysisResult}
                        isAnalyzingFile={isAnalyzingFile}
                        showCompleteAnalysis={showCompleteAnalysis}
                        setShowCompleteAnalysis={setShowCompleteAnalysis}
                        onFileUpload={handleFileUpload}
                        lastExtractedText={lastExtractedText}
                        isAiParsing={isAiParsing}
                        onAiParseClick={handleAiParseClick}
                      />
                      <AiAnalyzeSection
                        t={t}
                        isExpanded={sectionsExpanded.aiAnalyze}
                        onToggleExpanded={() => toggleSection('aiAnalyze')}
                        availableModels={availableModels}
                        onAnalyzeClick={handleAiAnalyzeClick}
                        onCompareClick={handleAiCompareClick}
                        onOptimizeClick={handleAiOptimizeClick}
                        isAiAnalyzing={isAiAnalyzing}
                        isAiComparing={isAiComparing}
                        isAiOptimizing={isAiOptimizing}
                        showAnalyzeCached={showAnalyzeCached}
                        showCompareCached={showCompareCached}
                        showOptimizeCached={showOptimizeCached}
                        aiAnalysisResult={aiAnalysisResult}
                        aiCompareResult={aiCompareResult}
                        aiOptimizeResult={aiOptimizeResult}
                        aiAnalysisStructured={aiAnalysisStructured}
                        aiCompareStructured={aiCompareStructured}
                        aiOptimizeStructured={aiOptimizeStructured}
                        isAutoIaRunning={isAutoIaRunning}
                        pipelineProgress={pipelineProgress}
                        onAutoIa={handleAutoIa}
                        effectiveJobDescription={effectiveJobDescription || ''}
                      />
                      <AtsGenerateSection
                        t={t}
                        isExpanded={sectionsExpanded.atsGenerate}
                        onToggleExpanded={() => toggleSection('atsGenerate')}
                        atsSectionConfig={atsSectionConfig}
                        setAtsSectionConfig={setAtsSectionConfig}
                        atsAdaptationLevel={atsAdaptationLevel}
                        setAtsAdaptationLevel={setAtsAdaptationLevel}
                        onApplyPreset={handlePresetAts}
                        isGenerating={isGenerating}
                        generatedCvData={generatedCvData}
                        generateChangesSummary={generateChangesSummary}
                        generateWarnings={generateWarnings}
                        onGenerateClick={handleGenerateClick}
                        onApplyGenerated={handleApplyGenerated}
                        data={data}
                        effectiveJobDescription={effectiveJobDescription || ''}
                      />
                      <CoverLetterSection
                        t={t}
                        isExpanded={sectionsExpanded.coverLetter}
                        onToggleExpanded={() => toggleSection('coverLetter')}
                        activeTemplate={activeTemplate}
                        templateMeta={templateMeta}
                        aiAnalysisStructured={aiAnalysisStructured}
                        aiCompareStructured={aiCompareStructured}
                        aiOptimizeStructured={aiOptimizeStructured}
                        isAnalyzeStale={isAnalyzeStale}
                        isCompareStale={isCompareStale}
                        isOptimizeStale={isOptimizeStale}
                        effectiveJobDescription={effectiveJobDescription || ''}
                        coverLetterLength={coverLetterLength}
                        setCoverLetterLength={setCoverLetterLength}
                        coverLetterFidelity={coverLetterFidelity}
                        setCoverLetterFidelity={setCoverLetterFidelity}
                        coverLetterTone={coverLetterTone}
                        setCoverLetterTone={setCoverLetterTone}
                        generatedCoverLetterByVersionId={generatedCoverLetterByVersionId}
                        setGeneratedCoverLetterByVersionId={setGeneratedCoverLetterByVersionId}
                        isGeneratingCoverLetter={isGeneratingCoverLetter}
                        onGenerateCoverLetter={handleCoverLetterClick}
                      />
                      <OptionsSection
                        t={t}
                        isMobile={false}
                        isExpanded={sectionsExpanded.options}
                        onToggleExpanded={() => toggleSection('options')}
                        srOnlyLabelsEnabled={srOnlyLabelsEnabled}
                        setSrOnlyLabelsEnabled={setSrOnlyLabelsEnabled}
                      />
                    </div>
                  </div>
                </div>
              )
            }
            preview={
              <PreviewPane
                t={cvT}
                isExporting={isExporting}
                isMobile={false}
                mobileTab="preview"
                mobilePreviewSubTab="apercu"
                isSidebarOpen={isSidebarOpen}
                setIsSidebarOpen={setIsSidebarOpen}
                isAtsSidebarOpen={isAtsSidebarOpen}
                setIsAtsSidebarOpen={setIsAtsSidebarOpen}
                theme={theme}
                setTheme={setTheme}
                locale={locale}
                setLocale={setLocale}
                authUser={authUser}
                authSignOut={authSignOut}
                setIsLoginModalOpen={setIsLoginModalOpen}
                data={data}
                commonInfo={commonInfo}
                fieldVisibility={fieldVisibility}
                layoutTemplate={layoutTemplate}
                previewEngine={previewEngine}
                srOnlyLabelsEnabled={srOnlyLabelsEnabled}
                getFormattedLocation={getFormattedLocation}
                getBornPrefix={getBornPrefix}
                getContactLineATS={getContactLineATS}
                calculateAge={calculateAge}
                formatDateWithFullMonth={formatDateCv}
                formatEducationDate={formatEducationDate}
                extractCityFromCompany={extractCityFromCompany}
                PAGE_HEIGHT_PX={PAGE_HEIGHT_PX}
                marginSize={marginSize}
                pageMode={pageMode}
                pagesToShow={pagesToShow}
                totalPages={totalPages}
                previewUserScale={previewUserScale}
                cvRef={cvRef}
                previewViewportRef={previewViewportRef}
                handlePreviewTouchStart={handlePreviewTouchStart}
                handlePreviewTouchEnd={handlePreviewTouchEnd}
              />
            }
            miseEnPagePanel={
              <MiseEnPagePanel
                t={t}
                isMiseEnPageOpen={isMiseEnPageOpen}
                layoutTemplate={layoutTemplate}
                setLayoutTemplate={setLayoutTemplate}
                previewEngine={previewEngine}
                setPreviewEngine={setPreviewEngine}
                maxPages={maxPages}
                setMaxPages={setMaxPages}
                marginSize={marginSize}
                setMarginSize={setMarginSize}
              />
            }
            actionRail={
              <SimpleActionRail
                isMiseEnPageOpen={isMiseEnPageOpen}
                onMiseEnPageToggle={() => setIsMiseEnPageOpen((prev) => !prev)}
              />
            }
          />
        )}

        {/* Simple mode focused modals */}
        <AdaptToJobModal
          open={activeSimpleModal === 'adapt'}
          onClose={handleCloseActiveModal}
          jobDescription={jobDescription}
          setJobDescription={setJobDescription}
          isAutoIaRunning={isAutoIaRunning}
          pipelineProgress={pipelineProgress}
          onRunAutoIa={guardedAutoIa}
          beforeData={data}
          generatedCvData={generatedCvData}
          onApplyPartial={handleApplyGeneratedPartial}
          activeVersionName={activeVersionName}
          onClearProgress={() => setPipelineProgress(null)}
          parsedJobOffer={parsedJobOffer}
          jobOffers={jobOffersHook.jobOffers}
          cvxjobEntries={jobOffersHook.cvxjobEntries}
          activeTemplate={activeTemplate}
          templateMeta={templateMeta}
          cvSkills={data.skills}
          autoRun={adaptAutoRun}
          onAutoRunConsumed={() => setAdaptAutoRun(false)}
          onSwitchToCv={(id) => setActiveTemplate(id)}
          generatedCoverLetterByVersionId={generatedCoverLetterByVersionId}
          onSaveOffer={(text) => {
            // addOffer handles deduplication by title+company internally
            jobOffersHook.addOffer({
              title: parsedJobOffer?.titre || 'Offre sans titre',
              company: parsedJobOffer?.entreprise || '',
              source: parsedJobOffer?.titre ? 'paste' : 'paste',
              rawText: text,
              parsedData: parsedJobOffer ?? undefined,
              isFavorite: false,
            });
          }}
        />
        <AtsScoreModal
          open={activeSimpleModal === 'score'}
          onClose={handleCloseActiveModal}
          aiAnalysisStructured={aiAnalysisStructured}
          isAiAnalyzing={isAiAnalyzing}
          onRunAnalyze={guardedAnalyze}
          onAdaptCV={() => setActiveSimpleModal('adapt')}
        />
        <TranslateModal
          open={activeSimpleModal === 'translate'}
          onClose={handleCloseActiveModal}
          activeTemplate={activeTemplate}
          isTranslatingVersionId={isTranslatingVersionId}
          onTranslate={handleTranslateVersion}
        />
        <CoverLetterModal
          open={activeSimpleModal === 'letter'}
          onClose={handleCloseActiveModal}
          activeTemplate={activeTemplate}
          effectiveJobDescription={effectiveJobDescription || ''}
          coverLetterLength={coverLetterLength}
          setCoverLetterLength={setCoverLetterLength}
          coverLetterFidelity={coverLetterFidelity}
          setCoverLetterFidelity={setCoverLetterFidelity}
          coverLetterTone={coverLetterTone}
          setCoverLetterTone={setCoverLetterTone}
          generatedCoverLetterByVersionId={generatedCoverLetterByVersionId}
          isGeneratingCoverLetter={isGeneratingCoverLetter}
          onGenerateCoverLetter={handleCoverLetterClick}
        />
        <ExportModal
          open={activeSimpleModal === 'export'}
          onClose={handleCloseActiveModal}
          data={data}
          onPrint={handlePrint}
          onExportDocx={handleExportDOCX}
          onExportJson={handleExportJSON}
          isExporting={isExporting}
        />
        <JobOffersModal
          open={activeSimpleModal === 'offers'}
          onClose={handleCloseActiveModal}
          jobOffers={jobOffersHook.jobOffers}
          cvxjobEntries={jobOffersHook.cvxjobEntries}
          templateMeta={templateMeta}
          onAddOffer={jobOffersHook.addOffer}
          onRemoveOffer={jobOffersHook.removeOffer}
          onToggleFavorite={jobOffersHook.toggleFavorite}
          onSelectOffer={(offer) => {
            // Pre-fill job description in the adapt modal, track selected offer for CVxJob recording
            setJobDescription(offer.rawText);
            setSelectedJobOfferId(offer.id);
            setAdaptAutoRun(true); // signal AdaptToJobModal to auto-start
            setActiveSimpleModal('adapt');
          }}
          activeTemplate={activeTemplate}
          isAiAnalyzing={isAiAnalyzing}
          onSwitchToCv={(id) => setActiveTemplate(id)}
          cvSkills={data.skills}
          onAnalyzeOffer={(offer) => {
            setJobDescription(offer.rawText);
            setSelectedJobOfferId(offer.id);
            // Don't close modal — spinner shows inline, score updates in list
            void guardedAnalyze();
          }}
        />

        {/* Content: sur mobile les panneaux démarrent sous le header (top explicite) */}
        <div className={`flex-1 min-h-0 flex flex-col md:flex-row relative ${isMobile ? 'mobile-content-below-header' : ''} ${!isMobile ? 'hidden' : ''}`}>
          {/* Sidebar Editor - on mobile: full-screen panel when tab = edition */}
          {!isExporting && (
            <EditSidebar
              t={t}
              isMobile={isMobile}
              mobileTab={mobileTab}
              isSidebarOpen={isSidebarOpen}
              setIsSidebarOpen={setIsSidebarOpen}
              data={data}
              commonInfo={commonInfo}
              fieldVisibility={fieldVisibility}
              setData={setData}
              editing={cvEditing}
              templateMeta={templateMeta}
              activeTemplate={activeTemplate}
              setActiveTemplate={setActiveTemplate}
              onRenameTemplate={renameTemplate}
              onDuplicateTemplate={handleDuplicateTemplate}
              onDeleteTemplate={handleDeleteTemplate}
            />
          )}

          {!isExporting && (
            <div
              className={`flex-shrink-0 transition-all duration-300 ease-in-out ${isMobile
                ? `absolute left-0 right-0 bottom-0 mobile-panel-top ${mobileTab === 'ats' ? 'z-10 flex flex-col' : 'z-0 opacity-0 pointer-events-none invisible'}`
                : isAtsSidebarOpen ? 'w-0 md:w-[400px]' : 'w-0 md:w-0'
                }`}
            >
              <button
                onClick={() => setIsAtsSidebarOpen((prev) => !prev)}
                className="absolute top-[65%] left-full -translate-y-1/2 -translate-x-1/2 z-30 w-8 h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 text-white/70 hover:text-white hidden md:flex items-center justify-center shadow-lg shadow-black/30"
                title={isAtsSidebarOpen ? t('nav.hideAts') : t('nav.showAts')}
              >
                {isAtsSidebarOpen ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
              </button>
              <div
                className={`${isMobile ? 'flex-1 min-h-0' : 'h-full min-h-0'} border-r border-white/10 glass ${isMobile ? '' : 'overflow-y-auto'} no-print scrollbar-hide transition-[opacity,transform] duration-300 ease-in-out ${isMobile ? 'mobile-scroll-panel' : ''} ${isAtsSidebarOpen || (isMobile && mobileTab === 'ats') ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2 pointer-events-none'
                  } ${isAtsSidebarOpen && !isMobile ? 'fixed inset-y-0 left-0 z-40 w-full max-w-[100vw] md:relative md:inset-auto md:z-0 md:max-w-none' : ''} ${isMobile ? 'relative' : ''}`}
              >
                <div className={`p-4 md:p-6 space-y-4 ${isMobile ? 'pt-3 pb-4 mobile-scroll-content' : ''}`}>
                  {/* Titre {t('sections.optimisationAts')} : masqué sur mobile (nav en haut) */}
                  <div className={`space-y-4 pt-2 ${isMobile ? 'hidden' : ''}`}>
                    <h1
                      className="flex items-center justify-between gap-2 text-xl font-black tracking-tight text-white cursor-pointer hover:text-emerald-300 transition-colors mb-2"
                      onClick={() => setIsAtsSidebarOpen((prev) => !prev)}
                    >
                      <span className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-emerald-600/20 flex items-center justify-center text-emerald-400">
                          <Target size={18} />
                        </div>
                        {t('sections.optimisationAts')}
                      </span>
                      {isAtsSidebarOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
                    </h1>
                  </div>
                  <JobTargetSection
                    t={t}
                    isMobile={isMobile}
                    isExpanded={sectionsExpanded.offreEmploiCible}
                    onToggleExpanded={() => toggleSection('offreEmploiCible')}
                    extensionInstalled={extensionInstalled}
                    onShowExtensionPage={() => setShowExtensionPage(true)}
                    jobInputMode={jobInputMode}
                    setJobInputMode={setJobInputMode}
                    parsedJobOffer={parsedJobOffer}
                    setParsedJobOffer={setParsedJobOffer}
                    jobDescription={jobDescription}
                    setJobDescription={setJobDescription}
                    jobUrl={jobUrl}
                    setJobUrl={setJobUrl}
                    jobTitle={jobTitle}
                    setJobTitle={setJobTitle}
                    canParseJobWithAi={canParseJobWithAi}
                    isParsingJobWithAi={isParsingJobWithAi}
                    onParseJobWithAi={handleParseJobWithAi}
                    isAutoIaRunning={isAutoIaRunning}
                    onAutoIa={handleAutoIa}
                    effectiveJobDescription={effectiveJobDescription || ''}
                  />

                  <AnalyseCompleteAtsSection
                    t={t}
                    isExpanded={sectionsExpanded.analyseCompleteAts}
                    onToggleExpanded={() => toggleSection('analyseCompleteAts')}
                    previewResult={previewResult}
                    completeAnalysisResult={completeAnalysisResult}
                    isAnalyzingFile={isAnalyzingFile}
                    showCompleteAnalysis={showCompleteAnalysis}
                    setShowCompleteAnalysis={setShowCompleteAnalysis}
                    onFileUpload={handleFileUpload}
                    lastExtractedText={lastExtractedText}
                    isAiParsing={isAiParsing}
                    onAiParseClick={handleAiParseClick}
                  />

                  <AiAnalyzeSection
                    t={t}
                    isExpanded={sectionsExpanded.aiAnalyze}
                    onToggleExpanded={() => toggleSection('aiAnalyze')}
                    availableModels={availableModels}
                    onAnalyzeClick={handleAiAnalyzeClick}
                    onCompareClick={handleAiCompareClick}
                    onOptimizeClick={handleAiOptimizeClick}
                    isAiAnalyzing={isAiAnalyzing}
                    isAiComparing={isAiComparing}
                    isAiOptimizing={isAiOptimizing}
                    showAnalyzeCached={showAnalyzeCached}
                    showCompareCached={showCompareCached}
                    showOptimizeCached={showOptimizeCached}
                    aiAnalysisResult={aiAnalysisResult}
                    aiCompareResult={aiCompareResult}
                    aiOptimizeResult={aiOptimizeResult}
                    aiAnalysisStructured={aiAnalysisStructured}
                    aiCompareStructured={aiCompareStructured}
                    aiOptimizeStructured={aiOptimizeStructured}
                    isAutoIaRunning={isAutoIaRunning}
                    pipelineProgress={pipelineProgress}
                    onAutoIa={handleAutoIa}
                    effectiveJobDescription={effectiveJobDescription || ''}
                  />
                  <AtsGenerateSection
                    t={t}
                    isExpanded={sectionsExpanded.atsGenerate}
                    onToggleExpanded={() => toggleSection('atsGenerate')}
                    atsSectionConfig={atsSectionConfig}
                    setAtsSectionConfig={setAtsSectionConfig}
                    atsAdaptationLevel={atsAdaptationLevel}
                    setAtsAdaptationLevel={setAtsAdaptationLevel}
                    onApplyPreset={handlePresetAts}
                    isGenerating={isGenerating}
                    generatedCvData={generatedCvData}
                    generateChangesSummary={generateChangesSummary}
                    generateWarnings={generateWarnings}
                    onGenerateClick={handleGenerateClick}
                    onApplyGenerated={handleApplyGenerated}
                    data={data}
                    effectiveJobDescription={effectiveJobDescription || ''}
                  />

                  <CoverLetterSection
                    t={t}
                    isExpanded={sectionsExpanded.coverLetter}
                    onToggleExpanded={() => toggleSection('coverLetter')}
                    activeTemplate={activeTemplate}
                    templateMeta={templateMeta}
                    aiAnalysisStructured={aiAnalysisStructured}
                    aiCompareStructured={aiCompareStructured}
                    aiOptimizeStructured={aiOptimizeStructured}
                    isAnalyzeStale={isAnalyzeStale}
                    isCompareStale={isCompareStale}
                    isOptimizeStale={isOptimizeStale}
                    effectiveJobDescription={effectiveJobDescription || ''}
                    coverLetterLength={coverLetterLength}
                    setCoverLetterLength={setCoverLetterLength}
                    coverLetterFidelity={coverLetterFidelity}
                    setCoverLetterFidelity={setCoverLetterFidelity}
                    coverLetterTone={coverLetterTone}
                    setCoverLetterTone={setCoverLetterTone}
                    generatedCoverLetterByVersionId={generatedCoverLetterByVersionId}
                    setGeneratedCoverLetterByVersionId={setGeneratedCoverLetterByVersionId}
                    isGeneratingCoverLetter={isGeneratingCoverLetter}
                    onGenerateCoverLetter={handleCoverLetterClick}
                  />

                  <OptionsSection
                    t={t}
                    isMobile={isMobile}
                    isExpanded={sectionsExpanded.options}
                    onToggleExpanded={() => toggleSection('options')}
                    srOnlyLabelsEnabled={srOnlyLabelsEnabled}
                    setSrOnlyLabelsEnabled={setSrOnlyLabelsEnabled}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Preview Panel */}
          <PreviewPane
            t={cvT}
            isExporting={isExporting}
            isMobile={isMobile}
            mobileTab={mobileTab}
            mobilePreviewSubTab={mobilePreviewSubTab}
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
            theme={theme}
            setTheme={setTheme}
            locale={locale}
            setLocale={setLocale}
            authUser={authUser}
            authSignOut={authSignOut}
            setIsLoginModalOpen={setIsLoginModalOpen}
            cvVersionCount={templateMeta.length}
            templateMeta={templateMeta}
            activeTemplate={activeTemplate}
            setActiveTemplate={setActiveTemplate}
            data={data}
            commonInfo={commonInfo}
            fieldVisibility={fieldVisibility}
            layoutTemplate={layoutTemplate}
            previewEngine={previewEngine}
            srOnlyLabelsEnabled={srOnlyLabelsEnabled}
            getFormattedLocation={getFormattedLocation}
            getBornPrefix={getBornPrefix}
            getContactLineATS={getContactLineATS}
            calculateAge={calculateAge}
            formatDateWithFullMonth={formatDateCv}
            formatEducationDate={formatEducationDate}
            extractCityFromCompany={extractCityFromCompany}
            PAGE_HEIGHT_PX={PAGE_HEIGHT_PX}
            marginSize={marginSize}
            pageMode={pageMode}
            pagesToShow={pagesToShow}
            totalPages={totalPages}
            previewUserScale={previewUserScale}
            cvRef={cvRef}
            previewViewportRef={previewViewportRef}
            handlePreviewTouchStart={handlePreviewTouchStart}
            handlePreviewTouchEnd={handlePreviewTouchEnd}
          />
          {/* Sidebar Visuel (desktop: à droite du preview, affichable/masquable) */}
          {!isExporting && (
            <VisualPanel
              t={t}
              isMobile={isMobile}
              mobileTab={mobileTab}
              mobilePreviewSubTab={mobilePreviewSubTab}
              isVisuelSidebarOpen={isVisuelSidebarOpen}
              setIsVisuelSidebarOpen={setIsVisuelSidebarOpen}
              layoutTemplate={layoutTemplate}
              setLayoutTemplate={setLayoutTemplate}
              previewEngine={previewEngine}
              setPreviewEngine={setPreviewEngine}
              maxPages={maxPages}
              setMaxPages={setMaxPages}
              marginSize={marginSize}
              setMarginSize={setMarginSize}
              exportMenuValue={exportMenuValue}
              setExportMenuValue={setExportMenuValue}
              isExportingPremium={isExportingPremium}
              setIsExportingPremium={setIsExportingPremium}
              data={data}
              fieldVisibility={fieldVisibility}
              handlePrint={handlePrint}
              handleExportDOCX={handleExportDOCX}
              handleExportJSON={handleExportJSON}
              getPrintFilename={getPrintFilename}
            />
          )}

          {/* Mobile bottom action bar */}
          {isMobile && (
            <MobileBottomBar
              t={t}
              onSwitchToPreview={() => setMobileTab('preview')}
              layoutTemplate={layoutTemplate}
              setLayoutTemplate={setLayoutTemplate}
              marginSize={marginSize}
              setMarginSize={setMarginSize}
              previewEngine={previewEngine}
              setPreviewEngine={setPreviewEngine}
            />
          )}
        </div>
        {/* ── Diff viewer after Auto IA ── */}
        {autoDiffData && (
          <Modal open size="xl" onClose={() => setAutoDiffData(null)}>
            <CvDiffViewer
              before={autoDiffData.before}
              after={autoDiffData.after}
              onApply={(merged) => {
                setData(merged);
                setAutoDiffData(null);
              }}
              onCancel={() => setAutoDiffData(null)}
            />
          </Modal>
        )}
        <LoginModal
          isOpen={isLoginModalOpen}
          onClose={() => setIsLoginModalOpen(false)}
          onLogin={async (email, password) => {
            const { error } = await signIn(email, password);
            if (error) throw error;
            const supabase = createClient();
            const {
              data: { session },
            } = await supabase.auth.getSession();
            const id = session?.user?.id;
            if (!id) throw new Error("Session introuvable après connexion");
            localStorage.setItem("cv_user_id", id);
            setUserId(id);
          }}
          onSignup={async (email, password) => {
            const res = await fetch("/api/auth/signup", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ email, password }),
            });
            const data = (await res.json()) as { error?: string; user?: { id: string } };
            if (!res.ok) throw new Error(data.error || "Inscription impossible");
            const { error } = await signIn(email, password);
            if (error) throw error;
            const supabase = createClient();
            const {
              data: { session },
            } = await supabase.auth.getSession();
            const id = session?.user?.id ?? data.user?.id;
            if (!id) throw new Error("Session introuvable après inscription");
            localStorage.setItem("cv_user_id", id);
            setUserId(id);
          }}
          onForgotPassword={async (email) => {
            const { error } = await resetPasswordForEmail(email);
            if (error) throw error;
          }}
        />
        {/* Phase 7 — Freemium gate */}
        {freemiumGateAction && (
          <FreemiumGate
            open={freemiumGateAction !== null}
            onClose={() => setFreemiumGateAction(null)}
            action={freemiumGateAction}
            onUpgrade={() => { creditsHook.upgradeToPremium(); setFreemiumGateAction(null); }}
          />
        )}
      </div>
      </NotifProvider>
      </ModalProvider>
    </I18nProvider>
  );
}

export default App;
